import { captureEvent, captureException } from '@sentry/browser'
import { externalApi } from '~/config'
import { NET_FUNNEL_COUPON_ENTRY_ERROR_MAP } from '@/assets/constants/error'

/** net-funnel 스크립트 동적 로딩
 * @returns {Promise}
 */
export const loadNfScript = () =>
  new Promise((resolve, reject) => {
    const { netFunnel } = externalApi
    if (!netFunnel) {
      resolve()
      return
    }
    if (!netFunnel || !netFunnel.src || !netFunnel.clientId || !netFunnel.projectKey) {
      reject(new Error('NetFunnel 설정값 오류'))
      return
    }
    if (document.querySelector('script[src*="netfunnel-javascript-agent.js"]')) {
      resolve()
      return
    }
    // script 로딩
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = netFunnel.src
    script.async = true
    script.setAttribute('data-nf-client-id', netFunnel.clientId)

    script.onload = () => resolve()
    script.onerror = () => {
      reject(new Error(' NetFunnel 스크립트 로딩 실패'))
    }
    document.head.appendChild(script)
  })

/** net-funnel 시작함수
 * @param {string} segmentKey - net-funnel 세그먼트 키
 * @returns {Promise}
 */
export const nfStartPromise = (segmentKey) =>
  new Promise((resolve, reject) => {
    const { netFunnel } = externalApi
    if (!netFunnel?.disabled || netFunnel?.disabled === true) {
      resolve({ status: 'Disabled' })
      return
    }
    if (!window.nfStart) {
      reject(new Error('nfStart 함수가 존재하지 않습니다'))
      return
    }
    if (!netFunnel?.projectKey || !segmentKey) {
      reject(new Error('net-funnel key 확인이 필요합니다.'))
      return
    }
    try {
      window.nfStart(
        {
          projectKey: netFunnel.projectKey,
          segmentKey,
        },
        (res) => {
          resolve(res)
        }
      )
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)))
    }
  })

/** net-funnel 종료함수
 * @param {string} segmentKey - net-funnel 세그먼트 키
 * @returns {Promise}
 */
export const nfStopPromise = (segmentKey) => {
  const { netFunnel } = externalApi
  return new Promise((resolve, reject) => {
    if (netFunnel?.disabled) {
      resolve()
      return
    }
    if (!window.nfStop) {
      reject(new Error('nfStop 함수가 존재하지 않습니다.'))
      return
    }
    if (!netFunnel?.projectKey || !segmentKey) {
      reject(new Error('net-funnel key 확인이 필요합니다.'))
      return
    }
    try {
      window.nfStop({
        projectKey: netFunnel.projectKey,
        segmentKey,
      })
      resolve()
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)))
    }
  })
}

/**
 * netFunnel 페이지 진입 처리 함수
 * @param {Object} route - 라우트 객체
 * @param {Object} app - Vue 앱 인스턴스
 * @param {Function} [next] - Vue Router next 함수
 * @returns {Object} { shouldStop: boolean } - 라우터 진행 중단 여부
 */
export const handleNetFunnelPageEntry = async (route, app) => {
  if (route.meta?.name !== 'new-coupon-list' || externalApi.netFunnel?.disabled) {
    return { shouldStop: false }
  }

  try {
    await loadNfScript()
  } catch (error) {
    captureEvent({
      message: 'netFunnel script 로딩 실패',
      level: 'fatal',
      fingerprint: ['netFunnel', 'script loading'],
      extra: {
        response: route.fullPath,
      },
    })
  }

  try {
    const segmentKey = externalApi.netFunnel.entrySegmentKey
    const response = await nfStartPromise(segmentKey)

    switch (response.status) {
      case 'Success':
      case 'Error':
      case 'NetworkError':
      case 'Block':
      case 'Disabled': // 커스텀 응답 case
        return { shouldStop: false }
      case 'IpBlock':
        app.$$alert(NET_FUNNEL_COUPON_ENTRY_ERROR_MAP.IP_BLOCK)
        return { shouldStop: true }
      case 'Close':
        app.$$alert(NET_FUNNEL_COUPON_ENTRY_ERROR_MAP.CLOSE)
        return { shouldStop: true }
      default:
        captureEvent({
          message: 'netFunnel 알 수 없는 응답',
          level: 'fatal',
          fingerprint: ['netFunnel', 'page-entry'],
          extra: {
            response: JSON.stringify(response),
          },
        })
        return { shouldStop: false }
    }
  } catch (error) {
    captureEvent({
      message: 'netFunnel 실행 오류 - 상품 상세 진입',
      level: 'fatal',
      fingerprint: ['netFunnel', 'page-entry'],
      extra: {
        error: error.message ?? '',
      },
    })
    return { shouldStop: false }
  }
}

/**
 * netFunnel 키 반납 타이머 설정
 * @param {string} segmentKey - net funnel 세그먼크 key
 * @param {number} delay - 지연 시간 (밀리초, 기본값: 8000)
 * @returns {number} 타이머 ID
 */
export const startNetFunnelStopTimer = (segmentKey, delay = 8000) =>
  setTimeout(async () => {
    try {
      await nfStopPromise(segmentKey)
    } catch (error) {
      captureEvent({
        message: 'netFunnel key 반납 오류',
        level: 'fatal',
        fingerprint: ['netFunnel', 'return-key'],
        extra: {
          nfStart: typeof window.nfStop,
          segmentKey,
        },
      })
    }
  }, delay)

/**
 * netFunnel 키 반납 타이머 해제 및 즉시 키 반납 처리
 * @param {string} segmentKey - net funnel 세그먼크 key
 * @param {number} timer - 타이머
 * @returns {number} 타이머 ID
 */
export const cancelNetFunnelStopTimer = async (segmentKey, timer) => {
  if (!segmentKey || !timer) {
    return
  }
  try {
    clearTimeout(timer)
    await nfStopPromise(segmentKey)
  } catch (error) {
    captureException(error, 'netFunnel Page Entry Stop 실패')
  }
}

/** net-funnel 버튼 클릭 시 응답 처리
 * @param {Object} response - netFunnel 콜백 응답데이터
 * @param {function} callback - netFunnel 통과 이후 실행해야할 함수
 * @return {string} - netFunnel 응답 데이터에 기반한 메시지 타입
 */
export const handleNFResponseOnClick = (response, callback) => {
  let messageType = ''
  switch (response.status) {
    case 'Success':
    case 'Error':
    case 'NetworkError':
    case 'Block':
      callback()
      break
    case 'IpBlock':
      messageType = 'IP_BLOCK'
      break
    case 'Close':
      messageType = 'CLOSE'
      break
    default:
      callback()
      captureEvent({
        message: 'netFunnel 알 수 없는 응답',
        level: 'fatal',
        fingerprint: ['netFunnel', 'button-click'],
        extra: {
          response: JSON.stringify(response),
        },
      })
      break
  }
  return messageType
}
